import { useState, useEffect } from 'react';
import Input, { Textarea } from '../ui/Input';
import KeywordInput from '../ui/KeywordInput';
import ColorPicker from '../ui/ColorPicker';
import ToggleGroup from '../ui/ToggleGroup';
import Slider from '../ui/Slider';
import Button from '../ui/Button';
import { SCALE_LABELS } from '../../constants/defaults';
import { validateBrandCore, validateVisuals, validateScales, hasErrors } from '../../utils/validation';
import './BriefEditor.css';

const colorModeOptions = [
  { value: 'light', label: 'Light', icon: '☀️' },
  { value: 'dark', label: 'Dark', icon: '🌙' }
];

const fontStyleOptions = [
  { value: 'serif', label: 'Serif' },
  { value: 'sans', label: 'Sans' },
  { value: 'mono', label: 'Mono' }
];

export default function BriefEditor({ brief, onSave, onCancel, saving }) {
  const [formData, setFormData] = useState({
    brandCore: { ...brief.brandCore },
    visuals: { ...brief.visuals },
    scales: { ...brief.scales }
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFormData({
      brandCore: { ...brief.brandCore },
      visuals: { ...brief.visuals },
      scales: { ...brief.scales }
    });
  }, [brief]);

  const updateField = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSave = () => {
    const allErrors = {
      ...validateBrandCore(formData.brandCore),
      ...validateVisuals(formData.visuals),
      ...validateScales(formData.scales)
    };

    if (hasErrors(allErrors)) {
      setErrors(allErrors);
      return;
    }

    onSave(formData);
  };

  return (
    <div className="brief-editor">
      <div className="brief-editor__header">
        <h2 className="brief-editor__title">Edit Brief</h2>
        <p className="brief-editor__subtitle">Update your brand's design preferences</p>
      </div>

      <div className="brief-editor__content">
        <section className="brief-editor__section">
          <h3 className="brief-editor__section-title">Brand Core</h3>
          <div className="brief-editor__fields">
            <Input
              label="Brand Name"
              value={formData.brandCore.name}
              onChange={(e) => updateField('brandCore', 'name', e.target.value)}
              error={errors.name}
              maxLength={100}
            />
            <Textarea
              label="Mission Statement"
              value={formData.brandCore.mission}
              onChange={(e) => updateField('brandCore', 'mission', e.target.value)}
              error={errors.mission}
              maxLength={280}
              rows={3}
            />
            <KeywordInput
              label="Vibe Keywords"
              values={formData.brandCore.vibeKeywords}
              onChange={(values) => updateField('brandCore', 'vibeKeywords', values)}
              error={errors.vibeKeywords}
            />
          </div>
        </section>

        <section className="brief-editor__section">
          <h3 className="brief-editor__section-title">Visuals</h3>
          <div className="brief-editor__fields">
            <ColorPicker
              label="Accent Color"
              value={formData.visuals.colorSeed}
              onChange={(color) => updateField('visuals', 'colorSeed', color)}
            />
            <ToggleGroup
              label="Color Mode"
              options={colorModeOptions}
              value={formData.visuals.colorMode}
              onChange={(mode) => updateField('visuals', 'colorMode', mode)}
            />
            <ToggleGroup
              label="Font Style"
              options={fontStyleOptions}
              value={formData.visuals.fontStyle}
              onChange={(style) => updateField('visuals', 'fontStyle', style)}
            />
          </div>
        </section>

        <section className="brief-editor__section">
          <h3 className="brief-editor__section-title">Design Scales</h3>
          <div className="brief-editor__fields brief-editor__fields--scales">
            <Slider
              label="Layout Density"
              labelLow={SCALE_LABELS.minimalistDense.low}
              labelHigh={SCALE_LABELS.minimalistDense.high}
              value={formData.scales.minimalistDense}
              onChange={(value) => updateField('scales', 'minimalistDense', value)}
            />
            <Slider
              label="Tone"
              labelLow={SCALE_LABELS.playfulSerious.low}
              labelHigh={SCALE_LABELS.playfulSerious.high}
              value={formData.scales.playfulSerious}
              onChange={(value) => updateField('scales', 'playfulSerious', value)}
            />
            <Slider
              label="Design Approach"
              labelLow={SCALE_LABELS.safeExperimental.low}
              labelHigh={SCALE_LABELS.safeExperimental.high}
              value={formData.scales.safeExperimental}
              onChange={(value) => updateField('scales', 'safeExperimental', value)}
            />
          </div>
        </section>
      </div>

      <div className="brief-editor__actions">
        <Button variant="secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} loading={saving}>
          Save Changes
        </Button>
      </div>
    </div>
  );
}
